import { FC, ReactNode, useState } from "react";
import { IAssistantData } from "../../../../entities/assistant";
import { documentsService, fileToBase64 } from "../../../../entities/documents";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { IIdmStaffMember } from "../../../../entities/idm";
import { IUserPersonalData } from "../../../../entities/user";
import { ConfirmationAction } from "../../../../shared/ui/confirmationAction";
import { Modal } from "../../../../shared/ui/modal";
import { MyTextarea } from "../../../../shared/ui/myTextarea";
import { OpenContainer } from "../../../../features/openContainer/ui/OpenContainer";
import { MyButton, MyButtonSecondary } from "../../../../shared/ui/button";
import { MoveUserWorkTg } from "../../../../features/moveUserWorkTg";
import dataListClasses from "../../../../shared/ui/dataList/dataList.module.scss";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import classes from "../staff/staffWidget.module.scss";
import { IStaffProfileSectionsContext } from "../../model/types";

interface IStaffProfileContentProps {
    staffMember: IIdmStaffMember;
    userPersonalData: IUserPersonalData | null;
    isTutorStaff: boolean;
    assistantProfile: IAssistantData | null;
    canEditStaff: boolean;
    canResetStaffPassword: boolean;
    canDeleteStaff: boolean;
    canArchiveStaff: boolean;
    canUnarchiveStaff: boolean;
    canMoveStaffWorkTg: boolean;
    onEdit: () => void;
    onResetPassword: () => void;
    onDelete: () => void;
    onArchiveSuccess: () => Promise<void>;
    onWorkTgMoveSuccess: () => Promise<void>;
    profileSectionsContext: IStaffProfileSectionsContext | null;
    renderProfileSections?: (context: IStaffProfileSectionsContext) => ReactNode;
}

export const StaffProfileContent: FC<IStaffProfileContentProps> = ({
    staffMember,
    userPersonalData,
    isTutorStaff,
    assistantProfile,
    canEditStaff,
    canResetStaffPassword,
    canDeleteStaff,
    canArchiveStaff,
    canUnarchiveStaff,
    canMoveStaffWorkTg,
    onEdit,
    onResetPassword,
    onDelete,
    onArchiveSuccess,
    onWorkTgMoveSuccess,
    profileSectionsContext,
    renderProfileSections,
}) => {
    const workTgs = userPersonalData?.work_tgs || assistantProfile?.work_tgs || []
    const [archiveOpen, setArchiveOpen] = useState(false)
    const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false)
    const [archiveComment, setArchiveComment] = useState('')
    const [archiveError, setArchiveError] = useState('')
    const [terminationFile, setTerminationFile] = useState<File | null>(null)
    const [isArchiveLoading, setIsArchiveLoading] = useState(false)
    const [isUnarchiveLoading, setIsUnarchiveLoading] = useState(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const isArchived = userPersonalData?.is_archive ?? staffMember.is_archive
    const mainWorkTg = workTgs[0]

    const getMskTodayIso = () => {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit',
        }).formatToParts(new Date())
        const getPart = (type: string) => parts.find(part => part.type === type)?.value || ''
        return `${getPart('year')}-${getPart('month')}-${getPart('day')}`
    }

    const openArchiveModal = () => {
        setArchiveComment('')
        setArchiveError('')
        setTerminationFile(null)
        setArchiveConfirmOpen(false)
        setArchiveOpen(true)
    }

    const prepareArchive = () => {
        if(!archiveComment.trim()){
            setArchiveError('Укажите причину архивации')
            return
        }
        setArchiveError('')
        setArchiveConfirmOpen(true)
    }

    const archiveStaff = async () => {
        try{
            setIsArchiveLoading(true)
            const terminationDocument = terminationFile ? {
                termination_file_name: terminationFile.name,
                termination_content_type: terminationFile.type || 'application/octet-stream',
                termination_content: await fileToBase64(terminationFile),
                termination_upload_at: getMskTodayIso(),
            } : {}
            await documentsService.archiveUser(staffMember.user_id, {
                comment: archiveComment.trim(),
                ...terminationDocument,
            })
            setArchiveOpen(false)
            await onArchiveSuccess()
            setGlobalMessage({message: 'Сотрудник архивирован', type: 'ok'})
        }
        catch(error){
            console.log(error)
            setArchiveConfirmOpen(false)
            setArchiveError('Не удалось архивировать сотрудника')
        }
        finally{
            setIsArchiveLoading(false)
        }
    }

    const unarchiveStaff = async () => {
        try{
            setIsUnarchiveLoading(true)
            await documentsService.unarchiveUser(staffMember.user_id)
            await onArchiveSuccess()
            setGlobalMessage({message: 'Сотрудник разархивирован', type: 'ok'})
        }
        catch(error){
            console.log(error)
            setGlobalMessage({message: 'Не удалось разархивировать сотрудника', type: 'error'})
        }
        finally{
            setIsUnarchiveLoading(false)
        }
    }

    return (
        <>
            {!isTutorStaff && (
                <section className={idmClasses.panel}>
                    <section className={idmClasses.header}>
                        <section className={classes.headerInfo}>
                            <section className={`${idmClasses.title} ${classes.staffTitle}`}>
                                <span>{userPersonalData?.user_id || staffMember.user_id}. {userPersonalData?.full_name || staffMember.full_name}</span>
                                <span
                                    className={isArchived ? classes.archivedStatusIcon : classes.activeStatusIcon}
                                    title={isArchived ? 'Сотрудник в архиве' : 'Сотрудник активен'}
                                    aria-label={isArchived ? 'Сотрудник в архиве' : 'Сотрудник активен'}
                                    data-status-tooltip={isArchived ? 'Сотрудник в архиве' : 'Сотрудник активен'}
                                >
                                    {isArchived
                                        ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6L18 18M18 6L6 18" /></svg>
                                        : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5L9.5 17L19 7" /></svg>}
                                </span>
                            </section>
                        </section>
                        <section className={idmClasses.headerActions}>
                            {canResetStaffPassword && (
                                <section className={idmClasses.resetPassword}>
                                    <MyButton onClick={onResetPassword}>Сбросить пароль</MyButton>
                                </section>
                            )}
                            {canEditStaff && (
                                <button type="button" className={classes.editAction} onClick={onEdit} title="Редактировать сотрудника" aria-label="Редактировать сотрудника">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 20H8L18.5 9.5C19.3284 8.67157 19.3284 7.32843 18.5 6.5V6.5C17.6716 5.67157 16.3284 5.67157 15.5 6.5L5 17V20Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"></path>
                                        <path d="M13.5 8.5L16.5 11.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"></path>
                                    </svg>
                                </button>
                            )}
                            {canArchiveStaff && !isArchived && (
                                <button type="button" className={classes.archiveAction} onClick={openArchiveModal} title="Архивировать сотрудника" aria-label="Архивировать сотрудника">
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M12 3V12" />
                                        <path d="M7.05 5.05A8 8 0 1 0 16.95 5.05" />
                                    </svg>
                                </button>
                            )}
                            {canUnarchiveStaff && isArchived && (
                                <button
                                    type="button"
                                    className={classes.unarchiveAction}
                                    onClick={unarchiveStaff}
                                    title="Разархивировать сотрудника"
                                    aria-label="Разархивировать сотрудника"
                                    disabled={isUnarchiveLoading}
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M12 3V12" />
                                        <path d="M7.05 5.05A8 8 0 1 0 16.95 5.05" />
                                    </svg>
                                </button>
                            )}
                            {canDeleteStaff && (
                                <button type="button" className={classes.deleteAction} onClick={onDelete} title="Удалить сотрудника" aria-label="Удалить сотрудника">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 7H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"></path>
                                        <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"></path>
                                        <path d="M6 7L7 20H17L18 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path>
                                        <path d="M9 7V4H15V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"></path>
                                    </svg>
                                </button>
                            )}
                        </section>
                    </section>
                    <section className={classes.dataOpenBlock}>
                        <OpenContainer
                            title="Данные"
                            icon={<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11V17M12 7H12.01" /></svg>}
                        >
                            <section className={classes.dataColumns}>
                                <section className={dataListClasses.container}>
                                    <ul className={dataListClasses.list}>
                                        <li>ID: {userPersonalData?.user_id || staffMember.user_id}</li>
                                        <li>ФИО: {userPersonalData?.full_name || assistantProfile?.full_name || staffMember.full_name}</li>
                                        <li>Дата регистрации: {userPersonalData?.created_at || assistantProfile?.created_at || 'Не указана'}</li>
                                        <li>Телефон: {userPersonalData?.phone || assistantProfile?.phone || staffMember.phone || 'Не указан'}</li>
                                        <li>Email: {userPersonalData?.email || staffMember.email || 'Не указан'}</li>
                                        <li>Баланс: {userPersonalData?.balance || '0'} ₽</li>
                                        {userPersonalData?.cost_per_shift && <li>Стоимость смены: {userPersonalData.cost_per_shift} ₽</li>}
                                        {userPersonalData?.cost_per_month && <li>Зарплата в месяц: {userPersonalData.cost_per_month} ₽</li>}
                                        <li>
                                            {userPersonalData?.tg || assistantProfile?.tg || staffMember.tg ? (
                                                <a
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    href={userPersonalData?.tg || assistantProfile?.tg || staffMember.tg}
                                                    className={classes.tgLink}
                                                >
                                                    <svg className={classes.tgLinkIcon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M6.33366 9.66634L9.66699 6.33301" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
                                                        <path d="M5.00033 4.33301H4.33366C3.59728 4.33301 3.00033 4.92996 3.00033 5.66634V11.6663C3.00033 12.4027 3.59728 12.9997 4.33366 12.9997H10.3337C11.07 12.9997 11.667 12.4027 11.667 11.6663V10.9997" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
                                                        <path d="M8.33301 3H12.9997V7.66667" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
                                                    </svg>
                                                    Написать в ТГ
                                                </a>
                                            ) : 'TG: не указан'}
                                        </li>
                                        <li>Роли: {userPersonalData?.roles?.map(role => role.role_name).join(', ') || staffMember.roles.map(role => role.role_name).join(', ') || 'Без ролей'}</li>
                                    </ul>
                                </section>
                                <section className={classes.workTgsColumn}>
                                    <section className={classes.workTgsTitle}>Доступные ТГ</section>
                                    {workTgs.length > 0 ? <ul className={classes.workTgsList}>
                                        {workTgs.map(workTg => <li key={workTg.id}>{workTg.name}</li>)}
                                    </ul> : <section className={classes.workTgsEmpty}>Не назначены</section>}
                                    {canMoveStaffWorkTg && mainWorkTg && (
                                        <section className={classes.workTgMove}>
                                            <MoveUserWorkTg
                                                userId={staffMember.user_id}
                                                currentTgId={mainWorkTg.id}
                                                currentTgName={mainWorkTg.name}
                                                onSuccess={onWorkTgMoveSuccess}
                                            />
                                        </section>
                                    )}
                                </section>
                            </section>
                        </OpenContainer>
                    </section>
                    <Modal setOpen={setArchiveOpen} open={archiveOpen}>
                        {archiveConfirmOpen ? (
                            <ConfirmationAction
                                onClick={archiveStaff}
                                setOpen={setArchiveOpen}
                                title="Вы точно хотите архивировать сотрудника?"
                                type="send"
                                isLoading={isArchiveLoading}
                            />
                        ) : (
                            <section className={classes.archiveModal}>
                                <h3>Причина архивации</h3>
                                <p>Укажите причину архивации. Договор о расторжении можно добавить при необходимости.</p>
                                <MyTextarea
                                    title="Причина"
                                    value={archiveComment}
                                    setValue={setArchiveComment}
                                    setError={setArchiveError}
                                    placeholder="Например: сотрудник больше не работает"
                                    rows={5}
                                />
                                <label className={classes.terminationField}>
                                    Договор о расторжении (необязательно)
                                    <input
                                        type="file"
                                        onChange={event => {
                                            setTerminationFile(event.target.files?.[0] || null)
                                            setArchiveError('')
                                        }}
                                    />
                                </label>
                                {archiveError && <section className={classes.archiveError}>*{archiveError}</section>}
                                <section className={classes.archiveButtons}>
                                    <MyButtonSecondary onClick={() => setArchiveOpen(false)}>Отмена</MyButtonSecondary>
                                    <MyButton disabled={!archiveComment.trim()} onClick={prepareArchive}>Продолжить</MyButton>
                                </section>
                            </section>
                        )}
                    </Modal>
                </section>
            )}
            {profileSectionsContext && renderProfileSections?.(profileSectionsContext)}
        </>
    )
}
